import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  FiUsers,
  FiShield,
  FiSave,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiUser,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiEye,
  FiEdit2,
  FiBriefcase,
  FiAlertCircle,
  FiLock,
  FiHome,
} from "react-icons/fi";
import axios from "axios";
import "./Settings.css";

const url = process.env.REACT_APP_BACKEND_URL;

// Allowed roles for system users
const SYSTEM_ROLE_OPTIONS = [
  { value: "developer", label: "Developer", color: "#f59e0b" },
  { value: "admin", label: "System Admin", color: "#3b82f6" },
  { value: "finance", label: "Finance", color: "#10b981" },
  { value: "HRM", label: "HR Manager", color: "#8b5cf6" },
  { value: "approve", label: "Approver", color: "#ef4444" }
];

// Company user roles
const COMPANY_ROLE_OPTIONS = [
  { value: "admin", label: "Company Admin", color: "#f59e0b" },
  { value: "sales", label: "Sales", color: "#3b82f6" },
  { value: "cashier", label: "Cashier", color: "#10b981" },
  { value: "purchase", label: "Purchase", color: "#8b5cf6" },
  { value: "user", label: "User", color: "#6b7280" }
];

const Settings = () => {
  const { 
    currentUser, 
    userType, 
    company, 
    loading: authLoading,
    canViewSettings,
    canManageSystemUsers,
    canManageCompanyUsers,
    isDeveloper,
    isSystemAdmin,
    isCompanyAdmin 
  } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(userType === 'company' ? "company-users" : "system-users");
  const [systemUsers, setSystemUsers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showInactive, setShowInactive] = useState(false);

  // Check if user can access settings
  useEffect(() => {
    if (!authLoading && currentUser) {
      // System users: only developer and admin can access
      if (userType === 'system' && !(currentUser.role === 'developer' || currentUser.role === 'admin')) {
        navigate('/unauthorized');
        return;
      }
      
      // Company users: only admin can access
      if (userType === 'company' && currentUser.role !== 'admin') {
        navigate('/unauthorized');
        return;
      }
      
      // Set default tab based on user type
      if (userType === 'company') {
        setActiveTab("company-users");
      } else {
        setActiveTab("system-users");
      }
    }
  }, [currentUser, authLoading, navigate, userType]);

  // Fetch system users (from User collection)
  const fetchSystemUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${url}/api/auth`);
      
      if (response.data.success) {
        setSystemUsers(response.data.data || []);
      } else {
        setError("Failed to fetch system users");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch system users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch company users
  const fetchCompanyUsers = async () => {
    setLoading(true);
    setError("");
    try {
      let endpoint = '';
      
      if (userType === 'company' && company) {
        // Company admin: fetch users from their company
        endpoint = `${url}/api/company/${company._id || company.id}/users`;
      } else if (userType === 'system') {
        // System admin/developer: try to fetch all company users
        endpoint = `${url}/api/company/users/all`;
      }
      
      if (!endpoint) {
        setError("No endpoint available for fetching company users");
        setLoading(false);
        return;
      }
      
      const response = await axios.get(endpoint);
      
      if (response.data.success) {
        let users = [];
        
        // Handle different response formats
        if (response.data.data && response.data.data.users) {
          users = response.data.data.users;
        } else if (Array.isArray(response.data.data)) {
          users = response.data.data;
        } else if (response.data.users) {
          users = response.data.users;
        }
        
        setCompanyUsers(users);
      } else {
        setError("Failed to fetch company users");
      }
    } catch (err) {     
      // If endpoint doesn't exist, try alternative approach for system admins
      if (userType === 'system' && err.response?.status === 404) {
        await fetchAllCompanyUsersFallback();
      } else {
        setError(err.response?.data?.message || "Failed to fetch company users");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fallback method to fetch all company users for system admins
  const fetchAllCompanyUsersFallback = async () => {
    try {
      const companiesRes = await axios.get(`${url}/api/company`);
      
      if (companiesRes.data.success) {
        const companiesList = companiesRes.data.data || [];
        let allUsers = [];
        
        // Fetch users for each company
        for (const comp of companiesList) {
          try {
            const usersRes = await axios.get(`${url}/api/company/${comp._id}/users`);
            
            if (usersRes.data.success) {
              const users = usersRes.data.data?.users || usersRes.data.data || [];
              // Add company info to each user
              const usersWithCompany = users.map(user => ({
                ...user,
                company_info: {
                  _id: comp._id,
                  company_name: comp.company_name,
                  company_code: comp.company_code
                }
              }));
              allUsers = [...allUsers, ...usersWithCompany];
            }
          } catch (err) {
          }
        }
        
        setCompanyUsers(allUsers);
      }
    } catch (err) {
      setError("Unable to fetch company users. Please check backend connectivity.");
    }
  };

  // Fetch companies for dropdown (system admins only)
  const fetchCompanies = async () => {
    if (userType !== 'system' || !(isDeveloper || isSystemAdmin)) {
      return;
    }
    
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (err) {
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (!currentUser || !canViewSettings()) return;
    
    if (activeTab === "system-users" && canManageSystemUsers()) {
      fetchSystemUsers();
    } else if (activeTab === "company-users" && canManageCompanyUsers()) {
      fetchCompanyUsers();
      if (userType === 'system' && (isDeveloper || isSystemAdmin)) {
        fetchCompanies();
      }
    }
  }, [activeTab, currentUser, userType, company, canViewSettings, canManageSystemUsers, canManageCompanyUsers]);

  // Get current users based on active tab
  const getCurrentUsers = () => {
    return activeTab === "system-users" ? systemUsers : companyUsers;
  };

  // Filter users based on search term and active status
  const filteredUsers = getCurrentUsers().filter((user) => {
    const matchesSearch = 
      (user.name || user.Usercomp_sure_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || user.Usercomp_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.Usercomp_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.company_info?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = showInactive ? true : 
      (activeTab === "system-users" ? user.isActive : user.Usercomp_status === "active");
    
    return matchesSearch && matchesStatus;
  });

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      let endpoint = activeTab === "system-users" 
        ? `${url}/api/auth/${userId}`
        : `${url}/api/usercomp/${userId}`;
      
      const response = await axios.delete(endpoint);
      
      if (response.data.success) {
        if (activeTab === "system-users") {
          setSystemUsers(systemUsers.filter(user => user._id !== userId));
        } else {
          setCompanyUsers(companyUsers.filter(user => user._id !== userId));
        }
        
        setSuccess("User deleted successfully");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError("Failed to delete user: " + (err.response?.data?.message || err.message));
      setTimeout(() => setError(""), 5000);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      if (activeTab === "system-users") {
        const response = await axios.patch(
          `${url}/api/auth/${userId}/status`,
          { isActive: !currentStatus }
        );
        
        if (response.data.success) {
          setSystemUsers(systemUsers.map(user => 
            user._id === userId ? { ...user, isActive: !currentStatus } : user
          ));
          setSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        }
      } else {
        const newStatus = currentStatus === "active" ? "inactive" : "active";
        const response = await axios.put(
          `${url}/api/usercomp/${userId}`,
          { Usercomp_status: newStatus }
        );
        
        if (response.data.success) {
          setCompanyUsers(companyUsers.map(user => 
            user._id === userId ? { ...user, Usercomp_status: newStatus } : user
          ));
          setSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        }
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to update user status");
      setTimeout(() => setError(""), 5000);
    }
  };

  // Handle view user
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setSelectedUser(user);
    
    // Prepare form data based on user type
    const formData = activeTab === "system-users" ? {
      _id: user._id,
      name: user.name,
      email: user.email,
      role: user.role,
      isActive: user.isActive
    } : {
      _id: user._id,
      name: user.Usercomp_sure_name,
      email: user.Usercomp_email,
      role: user.role,
      contact: user.Usercomp_contact,
      address: user.Usercomp_address,
      Usercomp_status: user.Usercomp_status,
      company: user.company?._id || user.company
    };
    
    setFormData(formData);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  // Handle save user
  const handleSaveUser = async () => {
    try {
      if (activeTab === "system-users") {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive
        };
        
        const response = await axios.put(
          `${url}/api/auth/${formData._id}`,
          updateData
        );
        
        if (response.data.success) {
          setSystemUsers(systemUsers.map(user => 
            user._id === formData._id ? response.data.data : user
          ));
          setSuccess("User updated successfully");
        }
      } else {
        const updateData = {
          Usercomp_sure_name: formData.name,
          Usercomp_email: formData.email,
          role: formData.role,
          Usercomp_contact: formData.contact,
          Usercomp_address: formData.address,
          Usercomp_status: formData.Usercomp_status
        };
        
        const response = await axios.put(
          `${url}/api/usercomp/${formData._id}`,
          updateData
        );
        
        if (response.data.success) {
          setCompanyUsers(companyUsers.map(user => 
            user._id === formData._id ? response.data.data : user
          ));
          setSuccess("User updated successfully");
        }
      }
      
      setIsModalOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user");
      setTimeout(() => setError(""), 5000);
    }
  };

  // Handle input change in modal
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setIsEditing(false);
    setFormData({});
  };

  // Render user modal
  const renderUserModal = () => {
    if (!selectedUser) return null;

    const isSystemUser = activeTab === "system-users";
    const roleOptions = isSystemUser ? SYSTEM_ROLE_OPTIONS : COMPANY_ROLE_OPTIONS;

    if (isEditing) {
      return (
        <div className="modal-content">
          <h2>{isSystemUser ? "Edit System User" : "Edit Company User"}</h2>

          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              placeholder="Full Name"
              required
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              placeholder="Email"
              required
            />
          </div>

          {!isSystemUser && (
            <>
              <div className="form-group">
                <label>Contact Number</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact || ""}
                  onChange={handleInputChange}
                  placeholder="Contact Number"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address || ""}
                  onChange={handleInputChange}
                  placeholder="Address"
                  rows="3"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Role *</label>
            <select
              name="role"
              value={formData.role || ""}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Role</option>
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {isSystemUser && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive || false}
                  onChange={handleInputChange}
                />
                Active Account
              </label>
            </div>
          )}

          {!isSystemUser && (
            <div className="form-group">
              <label>Status</label>
              <select
                name="Usercomp_status"
                value={formData.Usercomp_status || "active"}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className="modal-actions">
            <button className="btn-primary" onClick={handleSaveUser}>
              <FiSave /> Save Changes
            </button>
            <button className="btn-secondary" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // View mode
    return (
      <div className="modal-content">
        <div className="user-modal-header">
          <div className="user-avatar-large">
            {selectedUser.name 
              ? selectedUser.name.charAt(0).toUpperCase() 
              : selectedUser.Usercomp_sure_name 
                ? selectedUser.Usercomp_sure_name.charAt(0).toUpperCase() 
                : 'U'
            }
          </div>
          <div className="user-modal-info">
            <h2>{selectedUser.name || selectedUser.Usercomp_sure_name || "No Name"}</h2>
            <p>{selectedUser.email || selectedUser.Usercomp_email}</p>
          </div>
        </div>

        <div className="user-details">
          <div className="detail-item">
            <span className="detail-label">User Type:</span>
            <span className="detail-value">
              {activeTab === "system-users" ? "System User" : "Company User"}
            </span>
          </div>
          
          <div className="detail-item">
            <span className="detail-label">Role:</span>
            <span className="detail-value role-badge" style={{ 
              backgroundColor: roleOptions.find(r => r.value === selectedUser.role)?.color + '20',
              color: roleOptions.find(r => r.value === selectedUser.role)?.color,
              borderColor: roleOptions.find(r => r.value === selectedUser.role)?.color
            }}>
              {roleOptions.find(r => r.value === selectedUser.role)?.label || selectedUser.role}
            </span>
          </div>
          
          {!isSystemUser && (
            <>
              <div className="detail-item">
                <span className="detail-label">Contact:</span>
                <span className="detail-value">
                  {selectedUser.Usercomp_contact || "N/A"}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">User Code:</span>
                <span className="detail-value">
                  {selectedUser.Usercomp_code || "N/A"}
                </span>
              </div>
              
              {selectedUser.company_info && (
                <div className="detail-item">
                  <span className="detail-label">Company:</span>
                  <span className="detail-value">
                    {selectedUser.company_info.company_name} ({selectedUser.company_info.company_code})
                  </span>
                </div>
              )}
            </>
          )}
          
          <div className="detail-item">
            <span className="detail-label">Status:</span>
            <span className={`detail-value status-${isSystemUser 
              ? (selectedUser.isActive ? 'active' : 'inactive')
              : (selectedUser.Usercomp_status === 'active' ? 'active' : 'inactive')
            }`}>
              {isSystemUser 
                ? (selectedUser.isActive ? "Active" : "Inactive")
                : (selectedUser.Usercomp_status === 'active' ? "Active" : "Inactive")
              }
            </span>
          </div>
          
          <div className="detail-item">
            <span className="detail-label">Created:</span>
            <span className="detail-value">
              {selectedUser.createdAt 
                ? new Date(selectedUser.createdAt).toLocaleDateString() 
                : "N/A"
              }
            </span>
          </div>
          
          <div className="detail-item">
            <span className="detail-label">Last Login:</span>
            <span className="detail-value">
              {selectedUser.lastLogin 
                ? new Date(selectedUser.lastLogin).toLocaleString() 
                : "Never"
              }
            </span>
          </div>
        </div>

        <div className="modal-actions">
          {((isSystemUser && (isDeveloper || isSystemAdmin)) || 
            (!isSystemUser && (isDeveloper || isSystemAdmin || isCompanyAdmin))) && (
            <button className="btn-primary" onClick={() => handleEditUser(selectedUser)}>
              <FiEdit2 /> Edit User
            </button>
          )}
          <button className="btn-secondary" onClick={closeModal}>
            Close
          </button>
        </div>
      </div>
    );
  };

  // Render users tab
  const renderUsersTab = () => {
    const users = filteredUsers;
    const totalUsers = getCurrentUsers().length;
    const activeUsers = getCurrentUsers().filter(u => 
      activeTab === "system-users" ? u.isActive : u.Usercomp_status === "active"
    ).length;

    // Check if user has permission for this tab
    const hasTabPermission = activeTab === "system-users" 
      ? canManageSystemUsers() 
      : canManageCompanyUsers();

    if (!hasTabPermission) {
      return (
        <div className="settings-tab-content">
          <div className="unauthorized-access">
            <FiLock size={48} />
            <h3>Access Denied</h3>
            <p>You don't have permission to view {activeTab === "system-users" ? "system" : "company"} users.</p>
            <button 
              className="btn-primary" 
              onClick={() => navigate('/')}
            >
              <FiHome /> Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="settings-tab-content">
        <div className="tab-header">
          <h3>
            {activeTab === "system-users" ? "System User Management" : "Company User Management"}
            {userType === 'company' && company && (
              <span className="company-badge">
                <FiBriefcase /> {company.company_name}
              </span>
            )}
          </h3>
          <div className="header-actions">
            <button 
              className="btn-secondary" 
              onClick={activeTab === "system-users" ? fetchSystemUsers : fetchCompanyUsers}
              disabled={loading}
            >
              <FiRefreshCw className={loading ? "spinning" : ""} />
              Refresh
            </button>
            
            {activeTab === "system-users" && isDeveloper && (
              <button className="btn-primary">
                <Link to="/register" style={{ color: 'white', textDecoration: 'none' }}>
                  <FiPlus /> Add System User
                </Link>
              </button>
            )}
            
            {activeTab === "company-users" && (
              <button className="btn-primary">
                <Link 
                  to={userType === 'company' && company 
                    ? `/company/${company._id || company.id}/add-user`
                    : "/ViewCompany"
                  } 
                  style={{ color: 'white', textDecoration: 'none' }}
                >
                  <FiPlus /> Add Company User
                </Link>
              </button>
            )}
          </div>
        </div>

        <div className="search-filter-container">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab === "system-users" ? "system" : "company"} users...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span className="checkmark"></span>
              Show Inactive Users
            </label>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <FiAlertCircle /> {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            <FiCheckCircle /> {success}
          </div>
        )}

        {loading ? (
          <div className="loading">
            <FiRefreshCw className="spinning" /> 
            Loading {activeTab === "system-users" ? "system" : "company"} users...
          </div>
        ) : (
          <>
            <div className="users-stats">
              <div className="stat-item">
                <span className="stat-label">Total Users:</span>
                <span className="stat-value">{totalUsers}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Active:</span>
                <span className="stat-value">{activeUsers}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Showing:</span>
                <span className="stat-value">{users.length} users</span>
              </div>
            </div>

            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    {activeTab === "company-users" && <th>User Code</th>}
                    {activeTab === "company-users" && userType === 'system' && <th>Company</th>}
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === "company-users" ? (userType === 'system' ? "8" : "7") : "6"} className="no-users">
                        {searchTerm || showInactive
                          ? `No ${activeTab === "system-users" ? "system" : "company"} users found matching your criteria`
                          : `No ${activeTab === "system-users" ? "system" : "company"} users found`}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              {user.name 
                                ? user.name.charAt(0).toUpperCase() 
                                : user.Usercomp_sure_name 
                                  ? user.Usercomp_sure_name.charAt(0).toUpperCase() 
                                  : <FiUser />
                              }
                            </div>
                            <div className="user-name">
                              {user.name || user.Usercomp_sure_name || "No Name"}
                              {!user.name && !user.Usercomp_sure_name && (
                                <small className="text-muted">(ID: {user._id})</small>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{user.email || user.Usercomp_email}</td>
                        <td>
                          <span className={`role-badge role-${user.role}`}>
                            {(activeTab === "system-users" ? SYSTEM_ROLE_OPTIONS : COMPANY_ROLE_OPTIONS)
                              .find(r => r.value === user.role)?.label || user.role}
                          </span>
                        </td>
                        {activeTab === "company-users" && (
                          <td>
                            <code className="user-code">{user.Usercomp_code || "N/A"}</code>
                          </td>
                        )}
                        {activeTab === "company-users" && userType === 'system' && (
                          <td>
                            {user.company_info ? (
                              <span className="company-info">
                                {user.company_info.company_name}
                                <small>{user.company_info.company_code}</small>
                              </span>
                            ) : "N/A"}
                          </td>
                        )}
                        <td>
                          <button
                            className={`status-toggle ${
                              (activeTab === "system-users" ? user.isActive : user.Usercomp_status === "active") 
                                ? "active" 
                                : "inactive"
                            }`}
                            onClick={() => handleStatusToggle(
                              user._id, 
                              activeTab === "system-users" ? user.isActive : user.Usercomp_status
                            )}
                            title={
                              (activeTab === "system-users" ? user.isActive : user.Usercomp_status === "active") 
                                ? "Deactivate user" 
                                : "Activate user"
                            }
                            disabled={
                              user._id === currentUser?._id || 
                              (activeTab === "system-users" && !isDeveloper && !isSystemAdmin) ||
                              (activeTab === "company-users" && userType !== 'company' && !isDeveloper && !isSystemAdmin)
                            }
                          >
                            {(activeTab === "system-users" ? user.isActive : user.Usercomp_status === "active") 
                              ? <FiCheckCircle /> 
                              : <FiXCircle />
                            }
                            {(activeTab === "system-users" ? user.isActive : user.Usercomp_status === "active") 
                              ? "Active" 
                              : "Inactive"
                            }
                          </button>
                        </td>
                        <td>
                          {user.createdAt 
                            ? new Date(user.createdAt).toLocaleDateString('en-GB')
                            : "N/A"
                          }
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-icon view" 
                              title="View Details" 
                              onClick={() => handleViewUser(user)}
                            >
                              <FiEye />
                            </button>
                            <button 
                              className="btn-icon edit" 
                              title="Edit" 
                              onClick={() => handleEditUser(user)}
                              disabled={
                                user._id === currentUser?._id ||
                                (activeTab === "system-users" && !isDeveloper && !isSystemAdmin) ||
                                (activeTab === "company-users" && userType !== 'company' && !isDeveloper && !isSystemAdmin)
                              }
                            >
                              <FiEdit2 />
                            </button>
                            <button 
                              className="btn-icon delete" 
                              title="Delete" 
                              onClick={() => handleDeleteUser(user._id)}
                              disabled={
                                user._id === currentUser?._id ||
                                (activeTab === "system-users" && !isDeveloper) ||
                                (activeTab === "company-users" && userType !== 'company' && !isDeveloper)
                              }
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <FiRefreshCw className="spinning" size={32} />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  // Check if user can access settings
  if (!currentUser || !canViewSettings()) {
    return (
      <div className="settings-page">
        <div className="settings-header">
          <h1>Access Denied</h1>
          <p>You don't have permission to access user management settings.</p>
          <button 
            className="btn-primary" 
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            <FiHome /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>User Management Settings</h1>
        <p>
          {userType === 'system' ? 'Manage system and company users' : `Manage users for ${company?.company_name || 'your company'}`}
        </p>
      </div>

      <div className="settings-tabs">
        <div className="tabs-navigation">
          {(isDeveloper || isSystemAdmin) && (
            <button 
              className={`tab-btn ${activeTab === "system-users" ? "active" : ""}`} 
              onClick={() => setActiveTab("system-users")}
              disabled={!canManageSystemUsers()}
            >
              <FiShield /> System Users
              <span className="tab-count">{systemUsers.length}</span>
            </button>
          )}
          
          <button 
            className={`tab-btn ${activeTab === "company-users" ? "active" : ""}`} 
            onClick={() => setActiveTab("company-users")}
            disabled={!canManageCompanyUsers()}
          >
            <FiUsers /> Company Users
            <span className="tab-count">{companyUsers.length}</span>
          </button>
        </div>

        <div className="tabs-content">
          {renderUsersTab()}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            {renderUserModal()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;